/**
 * Lê as dimensões de um arquivo de imagem (File).
 */
export function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      resolve({ width: 0, height: 0 });
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Não foi possível carregar a imagem ${file.name}`));
    };
    img.src = url;
  });
}

export const PRODUTO_IMAGE_MAX_WIDTH = 1000;
export const PRODUTO_IMAGE_MAX_HEIGHT = 1000;

export const EVENTO_BANNER_WIDTH = 1450;
export const EVENTO_BANNER_HEIGHT = 400;
export const EVENTO_CARD_WIDTH = 300;
export const EVENTO_CARD_HEIGHT = 200;

/** Logo de patrocinador — proporção 16:9 (banner) */
export const PATROCINADOR_LOGO_WIDTH = 1440;
export const PATROCINADOR_LOGO_HEIGHT = 810;

export interface InvalidImageResult {
  file: File;
  width: number;
  height: number;
}

/**
 * Valida se todos os arquivos de imagem têm dimensões <= maxWidth e maxHeight.
 * Retorna lista de arquivos inválidos (que excedem o limite).
 */
export async function validateImageFilesMaxDimensions(
  files: File[],
  maxWidth: number = PRODUTO_IMAGE_MAX_WIDTH,
  maxHeight: number = PRODUTO_IMAGE_MAX_HEIGHT
): Promise<InvalidImageResult[]> {
  const invalid: InvalidImageResult[] = [];
  for (const file of files) {
    if (!file.type.startsWith("image/")) continue;
    try {
      const { width, height } = await getImageDimensions(file);
      if (width > maxWidth || height > maxHeight) {
        invalid.push({ file, width, height });
      }
    } catch {
      invalid.push({ file, width: 0, height: 0 });
    }
  }
  return invalid;
}

/**
 * Valida se todos os arquivos de imagem têm dimensões exatamente iguais ao alvo.
 * Retorna lista de arquivos inválidos (que não correspondem ao tamanho esperado).
 */
export async function validateImageFilesExactDimensions(
  files: File[],
  targetWidth: number = PATROCINADOR_LOGO_WIDTH,
  targetHeight: number = PATROCINADOR_LOGO_HEIGHT,
): Promise<InvalidImageResult[]> {
  const invalid: InvalidImageResult[] = [];
  for (const file of files) {
    if (!file.type.startsWith("image/")) continue;
    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== targetWidth || height !== targetHeight) {
        invalid.push({ file, width, height });
      }
    } catch {
      invalid.push({ file, width: 0, height: 0 });
    }
  }
  return invalid;
}
